import { Fragment, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Baby, ShieldCheck, Lock, CheckCircle2, Sparkles, Plus, X, FileDown, Send, Loader2, RefreshCw, AlertTriangle, MessageCircle } from "lucide-react";
import ConfirmCaptchaDialog from "@/components/ConfirmCaptchaDialog";

const GRADE_TONES = {
  "Excellent": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Very Good": "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "Good": "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  "Fair": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "Need Improvement": "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};

// Activities are graded directly with a letter (no marks). AB = Absent.
const ACTIVITY_GRADES = ["A+", "A", "B+", "B", "C", "D"];
const ACT_GRADE_TONES = {
  "A+": "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300",
  "A": "bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-900/40 dark:text-teal-300",
  "B+": "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-900/40 dark:text-sky-300",
  "B": "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300",
  "C": "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300",
  "D": "bg-rose-200 text-rose-800 border-rose-400 dark:bg-rose-900/60 dark:text-rose-200",
  "AB": "bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200",
};

const gradeFor = (marks, max = 25) => {
  if (marks == null) return null;
  const pct = (Number(marks) * 100) / max;
  if (pct >= 90) return "Excellent";
  if (pct >= 75) return "Very Good";
  if (pct >= 60) return "Good";
  if (pct >= 40) return "Fair";
  return "Need Improvement";
};

// Sub-category marks per subject (matches backend pre_primary_categories).
const ppCategories = (subject, examKey, className) => {
  if (examKey === "MIDTERM" || examKey === "ANNUAL") return ["Oral", "Dictation", "Writing"];
  const s = (subject || "").trim().toLowerCase();
  if (["kannada", "english", "hindi"].includes(s)) {
    // Play Group language subjects use a simpler 3 sub-skill set.
    return (className || "").trim() === "Play Group"
      ? ["Oral", "Dictation", "Written"]
      : ["Picture Reading", "Recognition", "Written", "Dictation"];
  }
  if (["maths", "mathematics", "math"].includes(s)) return ["Oral", "Recognition", "Written"];
  return [];
};

export default function PrePrimaryAssessments({
  embedded = false, classSel, secSel, setClassSel, setSecSel,
  classOptions, sectionOptions, isAdmin, isReadOnly, academicYear,
}) {
  const [meta, setMeta] = useState({ subjects: [], activities: [], current_key: null, max: 10 });
  const [rowKind, setRowKind] = useState("subject");
  const [rowName, setRowName] = useState("");
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [showActivities, setShowActivities] = useState(false);
  const [catDlg, setCatDlg] = useState(null); // {sid, name, roll, key, colShort}
  const [reportKey, setReportKey] = useState("");
  const [reportDl, setReportDl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  // Publish results to parents (WhatsApp) — mirrors classes 1-10
  const [pubExamKey, setPubExamKey] = useState("");
  const [pubData, setPubData] = useState(null);
  const [pubOpen, setPubOpen] = useState(false);
  const [pubResend, setPubResend] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubConfirmOpen, setPubConfirmOpen] = useState(false);
  const [isCT, setIsCT] = useState(false); // class teacher of this section (non-admin)

  const columns = grid?.columns || meta.columns || [];
  const currentKey = grid?.current_key ?? meta.current_key;
  const max = grid?.max || meta.max || 25;
  const colMax = (key) => columns.find((c) => c.key === key)?.max || max;

  // Load the subject/activity lists (+ current window) for this class/section.
  const loadMeta = async (kind, name) => {
    setLoading(true);
    try {
      const { data } = await api.get("/assessments/pre-primary", {
        params: { class_name: classSel, section: secSel, row: name || "", kind },
      });
      setMeta({ subjects: data.subjects || [], activities: data.activities || [],
        current_key: data.current_key, max: data.max, columns: data.columns });
      setIsCT(!!data.is_class_teacher);
      setGrid(data.row ? data : null);
      setEdits({});
      return data;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load assessment");
      setGrid(null);
      return null;
    } finally { setLoading(false); }
  };

  // When class/section change, reload lists and default to first subject.
  useEffect(() => {
    (async () => {
      const data = await loadMeta("subject", "");
      if (data) {
        const first = (data.subjects || [])[0] || "";
        setRowKind("subject");
        setRowName(first);
        if (first) loadMeta("subject", first);
      }
    })();
    // eslint-disable-next-line
  }, [classSel, secSel]);

  const pickRow = (kind, name) => {
    setRowKind(kind);
    setRowName(name);
    loadMeta(kind, name);
  };

  const setCell = (sid, key) => (e) => {
    const t = String(e.target.value).trim().toUpperCase();
    let patch;
    if (t === "") patch = { marks: null, absent: false };
    else if (t[0] === "A") patch = { marks: null, absent: true };
    else {
      const raw = t.replace(/[^0-9]/g, "");
      let n = raw === "" ? null : Number(raw);
      const cm = colMax(key);
      if (n != null && n > cm) n = cm;
      patch = { marks: n, absent: false };
    }
    setEdits((prev) => ({ ...prev, [`${sid}|${key}`]: { ...(prev[`${sid}|${key}`] || {}), ...patch } }));
  };

  const cellSrc = (sid, key) => {
    const e = edits[`${sid}|${key}`];
    return e || grid?.students?.find((x) => x.student_id === sid)?.cells?.[key] || {};
  };
  const cellDisplay = (sid, key) => {
    const src = cellSrc(sid, key);
    if (src.absent) return "AB";
    return src.marks == null ? "" : src.marks;
  };

  // Activities are graded directly with a letter. Value shown in the dropdown.
  const isActivity = rowKind === "activity";
  const setGrade = (sid, key) => (val) => {
    let patch;
    if (val === "__CLEAR__") patch = { grade: null, absent: false, marks: null };
    else if (val === "AB") patch = { grade: null, absent: true, marks: null };
    else patch = { grade: val, absent: false, marks: null };
    setEdits((prev) => ({ ...prev, [`${sid}|${key}`]: { ...(prev[`${sid}|${key}`] || {}), ...patch } }));
  };
  const gradeValue = (sid, key) => {
    const src = cellSrc(sid, key);
    if (src.absent) return "AB";
    return src.grade || "";
  };

  const dirtyCount = useMemo(() => Object.keys(edits).length, [edits]);

  const isCurrent = (col) => col.key === currentKey;
  const submittedKeys = grid?.submitted_keys || [];
  const rowSubmittedKeys = grid?.row_submitted_keys || [];
  const currentSubmitted = !!currentKey && submittedKeys.includes(currentKey);
  const currentRowSubmitted = !!currentKey && rowSubmittedKeys.includes(currentKey);
  const currentSub = (grid?.submissions || {})[currentKey] || null;
  const currentShort = columns.find((c) => c.key === currentKey)?.short || currentKey;
  const editableCol = (col) => {
    if (isReadOnly) return false;
    if (isAdmin) return true;
    if (!isCurrent(col)) return false;
    // Class teacher may modify ANY subject/activity in the open window (bypass
    // the subject-teacher submit lock), until the whole exam is forwarded to admin.
    if (isCT) return !submittedKeys.includes(col.key);
    return !submittedKeys.includes(col.key) && !rowSubmittedKeys.includes(col.key);
  };

  const save = async () => {
    if (isReadOnly) { toast.error("Super admin is read-only."); return; }
    const cells = Object.entries(edits).map(([k, v]) => {
      const [student_id, exam_key] = k.split("|");
      return { student_id, exam_key, marks: v.marks ?? null, absent: !!v.absent, categories: v.categories || null, grade: v.grade ?? null };
    });
    if (!cells.length) { toast.message("Nothing to save."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/assessments/pre-primary", {
        class_name: classSel, section: secSel, row: rowName, kind: rowKind, cells,
      });
      toast.success(`Saved ${data.saved} · cleared ${data.cleared}.`);
      await loadMeta(rowKind, rowName);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // stage per-category marks from the popup into local edits
  const applyCategories = (sid, key, cats, absent) => {
    const clean = {};
    let sum = 0;
    Object.entries(cats || {}).forEach(([k, v]) => {
      if (v !== "" && v != null && !Number.isNaN(Number(v))) { clean[k] = Number(v); sum += Number(v); }
    });
    const patch = absent
      ? { marks: null, absent: true, categories: null }
      : { marks: Object.keys(clean).length ? sum : null, absent: false, categories: Object.keys(clean).length ? clean : null };
    setEdits((prev) => ({ ...prev, [`${sid}|${key}`]: { ...(prev[`${sid}|${key}`] || {}), ...patch } }));
  };

  const effReportKey = reportKey || currentKey || (columns[0] || {}).key || "FA1";
  const downloadCard = async (studentId) => {
    setReportDl(studentId);
    try {
      const res = await api.get(`/assessments/pre-primary/report-card`, {
        params: { class_name: classSel, section: secSel, student_id: studentId, exam_key: effReportKey },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(cd);
      const fname = m ? decodeURIComponent(m[1]) : "progress-report.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not generate report card");
    } finally { setReportDl(""); }
  };

  // ---- Publish results to parents on WhatsApp (admin only) ----
  const effPubKey = pubExamKey || currentKey || (columns[0] || {}).key || "FA1";
  const loadPub = async () => {
    if (!isAdmin || !classSel || !secSel || !effPubKey) { setPubData(null); return; }
    try {
      const { data } = await api.get("/admin/pre-primary/results-overview", {
        params: { class_name: classSel, section: secSel, exam_key: effPubKey },
      });
      setPubData(data);
    } catch { setPubData(null); }
  };
  useEffect(() => { loadPub(); /* eslint-disable-next-line */ }, [isAdmin, classSel, secSel, effPubKey]);

  const pubStudents = pubData?.students || [];
  const eligible = useMemo(() => pubStudents.filter((s) => s.has_marks), [pubStudents]);
  const pending = useMemo(() => eligible.filter((s) => !s.published), [eligible]);
  const editedList = useMemo(() => pubStudents.filter((s) => s.edited), [pubStudents]);
  const dialogList = pubResend ? editedList : pending;

  const doPublish = async () => {
    const ids = dialogList.map((s) => s.student_id);
    if (ids.length === 0) return;
    setPubConfirmOpen(false);
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/publish-results", {
        class_name: classSel, section: secSel, exam_key: effPubKey,
        student_ids: ids, attach_report: true, resend: pubResend,
      });
      toast.success(`Sending ${data.queued ?? ids.length} progress card${(data.queued ?? ids.length) === 1 ? "" : "s"} to parents on WhatsApp…`);
      setPubOpen(false);
      setTimeout(loadPub, 4000);
      setTimeout(loadPub, 12000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not publish");
    } finally { setPublishing(false); }
  };

  const Shell = embedded ? Fragment : Layout;
  const marksOpen = !!currentKey;

  // ---- Teacher submits the CURRENT subject/activity to the class teacher ----
  const submitSubject = async () => {
    if (!currentKey || !rowName) return;
    if (dirtyCount > 0) { toast.error("Save your changes first, then submit."); return; }
    setSubmitting(true);
    try {
      await api.post("/assessments/pre-primary/submit-subject", {
        class_name: classSel, section: secSel, exam_key: currentKey, row: rowName, kind: rowKind,
      });
      toast.success(`${rowName} submitted to the class teacher.`);
      await loadMeta(rowKind, rowName);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  // ---- Admin reopens a submitted assessment so the teacher can edit again ----
  const reopenMarks = async (examKey) => {
    setReopening(true);
    try {
      await api.post("/assessments/pre-primary/reopen", {
        class_name: classSel, section: secSel, exam_key: examKey,
      });
      toast.success("Reopened — the class teacher can edit and re-submit.");
      await Promise.all([loadMeta(rowKind, rowName), loadPub()]);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not reopen");
    } finally { setReopening(false); }
  };


  return (
    <Shell>
      {!embedded && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-400 dark:text-pink-500 mb-2 flex items-center gap-1.5">
              <Baby className="h-3.5 w-3.5" /> Pre-Primary
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              Grade cards
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {academicYear ? `Academic year ${academicYear} · ` : ""}FA out of {colMax("FA1")} · Mid Term & Annual out of {colMax("MIDTERM")} · auto-graded
            </p>
          </div>
          <div
            className={`inline-flex items-center gap-2 rounded-full border px-3 h-9 shrink-0 ${
              marksOpen
                ? "border-emerald-300/70 bg-emerald-50 dark:bg-emerald-900/20"
                : "border-amber-300 bg-amber-50 dark:bg-amber-900/20"
            }`}
            data-testid="pp-window-banner"
            data-open={marksOpen ? "true" : "false"}
          >
            {marksOpen ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Lock className="h-3.5 w-3.5 text-amber-600" />}
            <span className={`text-xs font-semibold whitespace-nowrap ${marksOpen ? "text-emerald-800 dark:text-emerald-200" : "text-amber-900 dark:text-amber-200"}`}>
              {marksOpen ? <>Entry open · <b>{columns.find((c) => c.key === currentKey)?.short}</b></> : "Marks entry closed"}
            </span>
          </div>
        </div>
      )}

      {isAdmin && !isReadOnly && (
        <div className="mb-6 rounded-2xl border border-pink-300/70 dark:border-pink-800 bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white px-4 sm:px-5 py-3 flex items-center gap-3" data-testid="pp-admin-banner">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>Admin edit mode — correct any FA anytime</div>
            <div className="text-xs text-white/85 mt-0.5">Teachers enter marks only during the open window; you can edit every FA column.</div>
          </div>
          <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => setShowActivities(true)} data-testid="pp-edit-activities-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> Activities
          </Button>
        </div>
      )}

      {isCT && !isAdmin && !isReadOnly && (
        <div className="mb-6 rounded-2xl border border-indigo-300/70 dark:border-indigo-800 bg-gradient-to-r from-indigo-500 to-violet-600 text-white px-4 sm:px-5 py-3 flex items-center gap-3" data-testid="pp-ct-banner">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>Class teacher edit mode</div>
            <div className="text-xs text-white/85 mt-0.5">You can modify marks &amp; grades for <b>every subject and activity</b> in the open window — even after a subject teacher has submitted.</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Field label="Class">
          <Select value={classSel} onValueChange={setClassSel}>
            <SelectTrigger className="h-11" data-testid="pp-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Section">
          <Select value={secSel} onValueChange={setSecSel}>
            <SelectTrigger className="h-11" data-testid="pp-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Subject / Activity">
          <Select value={rowName ? `${rowKind}:${rowName}` : ""} onValueChange={(v) => { const [k, ...rest] = v.split(":"); pickRow(k, rest.join(":")); }}>
            <SelectTrigger className="h-11" data-testid="pp-row-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {meta.subjects.length > 0 && <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">Subjects</div>}
              {meta.subjects.map((s) => <SelectItem key={`subject:${s}`} value={`subject:${s}`}>{s}</SelectItem>)}
              {meta.activities.length > 0 && <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 mt-1">Activities</div>}
              {meta.activities.map((a) => <SelectItem key={`activity:${a}`} value={`activity:${a}`}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Actions">
          <Button
            onClick={save}
            disabled={saving || isReadOnly || dirtyCount === 0 || (!isAdmin && !currentKey)}
            className="h-11 w-full bg-pink-500 hover:bg-pink-600 disabled:opacity-60"
            data-testid="pp-save-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Saving…" : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`}
          </Button>
        </Field>
      </div>

      {/* Submit each subject/activity to the class teacher (Play Group / LKG / UKG) */}
      {!isAdmin && !isReadOnly && classSel && secSel && rowName && (
        <div
          className={`mb-4 rounded-2xl border px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 ${
            currentSubmitted
              ? "border-emerald-300/70 bg-emerald-50/70 dark:bg-emerald-900/15"
              : currentRowSubmitted
              ? "border-emerald-300/70 bg-emerald-50/70 dark:bg-emerald-900/15"
              : marksOpen
              ? "border-pink-200 dark:border-pink-900/60 bg-pink-50/60 dark:bg-pink-900/10"
              : "border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-900/10"
          }`}
          data-testid="pp-submit-panel"
        >
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${(currentSubmitted || currentRowSubmitted) ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-pink-500/15 text-pink-600 dark:text-pink-400"}`}>
            {(currentSubmitted || currentRowSubmitted) ? <ShieldCheck className="h-5 w-5" /> : <Send className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100 leading-tight" style={{ fontFamily: "Outfit" }}>
              {currentSubmitted
                ? `Forwarded to admin · ${currentShort}`
                : currentRowSubmitted
                ? `Submitted · ${rowName} · ${currentShort}`
                : `Submit ${rowName} to the class teacher`}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-testid="pp-submit-status">
              {currentSubmitted
                ? <>The class teacher has forwarded {currentShort} to the admin. Ask the admin to reopen if you need to edit.</>
                : currentRowSubmitted
                ? <><b>{rowName}</b> is submitted &amp; locked. Now open <b>Class Results</b> to forward all subjects to the admin.</>
                : marksOpen
                ? <>Enter <b>{rowName}</b> for every pupil, Save, then submit it. Do the same for each subject &amp; activity, then forward from <b>Class Results</b>.</>
                : "Marks entry is closed. The admin will open the next assessment window."}
            </div>
          </div>
          {(currentSubmitted || currentRowSubmitted) ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold shrink-0" data-testid="pp-submitted-badge">
              <Lock className="h-3.5 w-3.5" /> Submitted &amp; locked
            </span>
          ) : (
            <Button
              onClick={submitSubject}
              disabled={submitting || !marksOpen || dirtyCount > 0}
              className="h-10 bg-pink-600 hover:bg-pink-700 disabled:opacity-60 shrink-0"
              title={!marksOpen ? "No assessment is open for entry" : dirtyCount > 0 ? "Save your changes first" : `Submit ${rowName} to the class teacher`}
              data-testid="pp-submit-btn"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Submitting…</> : <><Send className="h-4 w-4 mr-1.5" /> Submit {rowName}</>}
            </Button>
          )}
        </div>
      )}

      {/* Publish results to parents (WhatsApp) — admin only */}
      {isAdmin && classSel && secSel && (
        <div className="mb-4 rounded-2xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-900/15 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="pp-publish-panel">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 dark:text-slate-100 leading-tight" style={{ fontFamily: "Outfit" }}>Publish progress cards to parents</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-testid="pp-publish-summary">
              {pubData
                ? <>{pubData.submitted && <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Teacher submitted{pubData.submitted_by ? ` by ${pubData.submitted_by}` : ""} · </span>}{pubData.published_count} of {eligible.length} published{pending.length > 0 && <span className="text-amber-600 dark:text-amber-400"> · {pending.length} pending</span>}{editedList.length > 0 && <span className="text-sky-600 dark:text-sky-400"> · {editedList.length} corrected</span>}</>
                : "Loading…"}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <select value={effPubKey} onChange={(e) => setPubExamKey(e.target.value)}
              className="h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm" data-testid="pp-publish-exam-select">
              {columns.map((c) => <option key={c.key} value={c.key}>{c.short}</option>)}
            </select>
            {pubData?.submitted && (
              <Button size="sm" variant="outline" className="h-9 border-pink-300 text-pink-700 dark:text-pink-300"
                onClick={() => reopenMarks(effPubKey)} disabled={reopening} data-testid="pp-reopen-btn"
                title="Reopen this assessment so the class teacher can edit and re-submit">
                {reopening ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Reopen for teacher
              </Button>
            )}
            {editedList.length > 0 && (
              <Button size="sm" variant="outline" className="h-9 border-sky-300 text-sky-700 dark:text-sky-300"
                onClick={() => { setPubResend(true); setPubOpen(true); }} data-testid="pp-resend-btn">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Re-send ({editedList.length})
              </Button>
            )}
            <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              disabled={pending.length === 0}
              onClick={() => { setPubResend(false); setPubOpen(true); }} data-testid="pp-publish-btn"
              title={pending.length === 0 ? "All eligible pupils are already published" : undefined}>
              <Send className="h-4 w-4 mr-1.5" />
              {eligible.length > 0 && pending.length === 0 ? `All published (${eligible.length})` : `Publish to parents (${pending.length})`}
            </Button>
          </div>
        </div>
      )}

      {/* Publish confirm dialog */}
      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pp-publish-dialog">
          <DialogHeader>
            <DialogTitle>{pubResend ? "Re-send corrected progress cards" : "Publish progress cards to parents"}</DialogTitle>
            <DialogDescription>
              {pubResend
                ? "These pupils' marks were corrected since they were last published. A fresh WhatsApp with the updated progress card will be sent to each parent."
                : `A WhatsApp message with each child's progress card (${pubData?.exam_label || effPubKey}) will be sent to their parent. Already-published pupils are skipped automatically.`}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 max-h-[46vh] overflow-y-auto" data-testid="pp-publish-list">
            {dialogList.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">No pupils to send.</div>
            ) : dialogList.map((s) => (
              <div key={s.student_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`pp-publish-row-${s.roll_number}`}>
                <span className="w-8 text-slate-400 shrink-0">{s.roll_number}</span>
                <span className="font-medium text-slate-800 dark:text-slate-100 flex-1 min-w-0 truncate">{s.name}</span>
                {s.parent_phone
                  ? <span className="text-xs text-slate-500">{s.parent_phone}</span>
                  : <span className="text-xs text-rose-500 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> No parent phone</span>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPubOpen(false)} disabled={publishing} data-testid="pp-publish-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPubConfirmOpen(true)} disabled={publishing || dialogList.length === 0} data-testid="pp-publish-confirm">
              {publishing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1.5" /> Send {dialogList.length} to WhatsApp</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmCaptchaDialog
        isOpen={pubConfirmOpen}
        onClose={() => setPubConfirmOpen(false)}
        onConfirm={doPublish}
        busy={publishing}
        title={pubResend ? "Confirm re-send to parents" : "Confirm publish to parents"}
        description={`This will ${pubResend ? "re-send WhatsApp progress cards to" : "send WhatsApp progress cards to"} ${dialogList.length} parent${dialogList.length === 1 ? "" : "s"}. Enter the 4-digit code to confirm.`}
        confirmLabel={pubResend ? `Re-send to ${dialogList.length}` : `Send to ${dialogList.length}`}
      />

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden" data-testid="pp-grid">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !rowName ? (
          <div className="p-10 text-center text-slate-500" data-testid="pp-no-row">No subjects configured for this class. Mark subjects mandatory in Settings → Subjects.</div>
        ) : !grid || grid.students.length === 0 ? (
          <div className="p-12 text-center" data-testid="pp-empty">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center mb-3">
              <Baby className="h-6 w-6" />
            </div>
            <div className="text-slate-700 dark:text-slate-200 font-semibold" style={{ fontFamily: "Outfit" }}>No students in {classSel}-{secSel}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="px-4 pt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="pp-hint">
              {isActivity ? (
                <>Grading <b className="text-pink-600">{rowName}</b> · pick a grade for each pupil (<b>A+ · A · B+ · B · C · D</b>) or mark <b className="text-rose-600">Absent</b>.</>
              ) : (
                <>Entering <b className="text-pink-600">{rowName}</b> · type the marks for each column (or <b className="text-rose-600">A</b> for Absent). Grade shows automatically.</>
              )}
            </div>
            <div className="px-4 pb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400" data-testid="pp-report-bar">
              <FileDown className="h-3.5 w-3.5 text-pink-500" />
              Progress report card for
              <select value={effReportKey} onChange={(e) => setReportKey(e.target.value)}
                className="h-8 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2" data-testid="pp-report-exam-select">
                {columns.map((c) => <option key={c.key} value={c.key}>{c.short}</option>)}
              </select>
              <span className="text-slate-400">— tap the download icon on any pupil row.</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50 dark:bg-slate-900/40 z-10 w-52">Roll · Student</th>
                  {columns.map((col) => (
                    <th key={col.key} className={`text-center px-3 py-3 ${isCurrent(col) ? "text-pink-700 dark:text-pink-300 bg-pink-50 dark:bg-pink-900/20" : "text-slate-500 dark:text-slate-400"}`} data-testid={`pp-col-${col.key}`}>
                      <div className="text-xs font-semibold uppercase tracking-wide flex items-center justify-center gap-1">
                        {isCurrent(col) && <CheckCircle2 className="h-3 w-3" />}
                        {col.short}
                      </div>
                      <div className="text-[10px] font-normal normal-case text-slate-400 mt-0.5">{isActivity ? "grade" : `out of ${col.max}`}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.students.map((s, idx) => (
                  <tr key={s.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""}`}>
                    <td className="px-4 py-2 sticky left-0 bg-inherit z-10">
                      <div className="flex items-center gap-2" data-testid={`pp-student-${s.roll_number}`}>
                        <div className="h-8 w-8 rounded-lg bg-pink-50 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 flex items-center justify-center text-xs font-semibold shrink-0">
                          {s.roll_number?.slice(-2) || idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{s.name}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">{s.roll_number}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadCard(s.student_id)}
                          disabled={reportDl === s.student_id}
                          title="Download progress report card"
                          className="h-7 w-7 rounded-lg text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/30 flex items-center justify-center shrink-0 disabled:opacity-50"
                          data-testid={`pp-report-dl-${s.student_id}`}
                        >
                          <FileDown className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    {columns.map((col) => {
                      const editable = editableCol(col);
                      const k = `${s.student_id}|${col.key}`;
                      const dirty = !!edits[k];
                      const src = cellSrc(s.student_id, col.key);
                      // ---- Activities: direct letter-grade dropdown (no marks) ----
                      if (isActivity) {
                        const gv = gradeValue(s.student_id, col.key);
                        const tone = ACT_GRADE_TONES[gv] || "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100";
                        return (
                          <td key={col.key} className="px-2 py-2">
                            <div className="flex flex-col items-center gap-1">
                              <select
                                disabled={!editable}
                                value={gv}
                                onChange={(e) => setGrade(s.student_id, col.key)(e.target.value)}
                                title="Pick a grade, or Absent"
                                className={`h-9 w-[74px] rounded-md border text-center text-sm font-semibold cursor-pointer transition-colors ${dirty ? "ring-2 ring-amber-200 border-amber-400" : ""} ${!editable ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-900/40 text-slate-500" : tone}`}
                                data-testid={`pp-grade-select-${s.student_id}-${col.key}`}
                              >
                                <option value="">—</option>
                                {ACTIVITY_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                                <option value="AB">AB · Absent</option>
                                {gv !== "" && <option value="__CLEAR__">Clear</option>}
                              </select>
                              {src.absent
                                ? <span className="text-[10px] font-semibold text-rose-500" data-testid={`pp-grade-${s.student_id}-${col.key}`}>Absent</span>
                                : <span className="text-[10px] text-transparent">—</span>}
                            </div>
                          </td>
                        );
                      }
                      const disp = cellDisplay(s.student_id, col.key);
                      const grade = src.absent ? null : gradeFor(src.marks, colMax(col.key));
                      const cats = ppCategories(rowName, col.key, classSel);
                      const hasCats = cats.length > 0;
                      return (
                        <td key={col.key} className="px-2 py-2">
                          <div className="flex flex-col items-center gap-1">
                            {hasCats ? (
                              <button
                                type="button"
                                disabled={!editable}
                                onClick={() => setCatDlg({ sid: s.student_id, name: s.name, roll: s.roll_number, key: col.key, colShort: col.short })}
                                title="Click to enter category marks"
                                className={`h-9 w-16 rounded-md border text-center text-sm flex items-center justify-center transition-colors ${dirty ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200 dark:border-slate-700"} ${!editable ? "bg-slate-50 dark:bg-slate-900/40 text-slate-500 cursor-not-allowed" : "bg-white dark:bg-slate-900 hover:border-pink-400 hover:bg-pink-50/50 dark:hover:bg-pink-900/20 cursor-pointer"} ${disp === "AB" ? "text-rose-600 font-semibold uppercase" : "text-slate-800 dark:text-slate-100 font-medium"}`}
                                data-testid={`pp-marks-${s.student_id}-${col.key}`}
                              >
                                {disp === "" ? "—" : disp}
                              </button>
                            ) : (
                              <Input
                                type="text"
                                disabled={!editable}
                                value={disp}
                                onChange={setCell(s.student_id, col.key)}
                                placeholder="—"
                                title="Enter the marks, or type A for Absent"
                                className={`h-9 w-16 text-center text-sm ${dirty ? "border-amber-400 ring-2 ring-amber-100" : ""} ${!editable ? "bg-slate-50 dark:bg-slate-900/40 text-slate-500" : ""} ${disp === "AB" ? "text-rose-600 font-semibold uppercase" : ""}`}
                                data-testid={`pp-marks-${s.student_id}-${col.key}`}
                              />
                            )}
                            {src.absent ? (
                              <span className="text-[10px] font-semibold text-rose-500" data-testid={`pp-grade-${s.student_id}-${col.key}`}>Absent</span>
                            ) : grade ? (
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${GRADE_TONES[grade]}`} data-testid={`pp-grade-${s.student_id}-${col.key}`}>{grade}</span>
                            ) : (
                              <span className="text-[10px] text-transparent">—</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isReadOnly && (
        <div className="mt-4 text-xs text-purple-600 dark:text-purple-300 inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Super admin · read-only access.
        </div>
      )}

      <ActivitiesDialog
        open={showActivities}
        onClose={() => setShowActivities(false)}
        classSel={classSel}
        activities={meta.activities}
        onSaved={(acts) => { setMeta((m) => ({ ...m, activities: acts })); setShowActivities(false); }}
      />

      <CategoryDialog
        info={catDlg}
        subject={rowName}
        className={classSel}
        max={catDlg ? colMax(catDlg.key) : max}
        current={catDlg ? cellSrc(catDlg.sid, catDlg.key) : {}}
        onClose={() => setCatDlg(null)}
        onApply={(cats, absent) => { applyCategories(catDlg.sid, catDlg.key, cats, absent); setCatDlg(null); }}
      />
    </Shell>
  );
}

function CategoryDialog({ info, subject, max, current, onClose, onApply, className }) {
  const cats = info ? ppCategories(subject, info.key, className) : [];
  const [vals, setVals] = useState({});
  const [absent, setAbsent] = useState(false);

  useEffect(() => {
    if (!info) return;
    const src = current?.categories || {};
    const init = {};
    cats.forEach((c) => { init[c] = src[c] != null ? String(src[c]) : ""; });
    setVals(init);
    setAbsent(!!current?.absent);
    // eslint-disable-next-line
  }, [info]);

  const sum = cats.reduce((a, c) => a + (vals[c] !== "" && !Number.isNaN(Number(vals[c])) ? Number(vals[c]) : 0), 0);
  const over = sum > max;

  const setV = (c) => (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setVals((p) => ({ ...p, [c]: raw }));
    if (raw !== "") setAbsent(false);
  };

  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pp-category-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Baby className="h-5 w-5 text-pink-600" /> {subject} · {info?.colShort}
          </DialogTitle>
          <DialogDescription>
            {info?.name} · Roll {info?.roll} — enter marks for each category (total out of {max}).
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-2 ${absent ? "opacity-40 pointer-events-none" : ""}`}>
          {cats.map((c) => (
            <div key={c} className="flex items-center justify-between gap-3" data-testid={`pp-cat-row-${c}`}>
              <label className="text-sm text-slate-700 dark:text-slate-200">{c}</label>
              <Input
                value={vals[c] ?? ""}
                onChange={setV(c)}
                inputMode="decimal"
                placeholder="0"
                className="h-9 w-24 text-center"
                data-testid={`pp-cat-input-${c}`}
              />
            </div>
          ))}
        </div>

        <div className={`flex items-center justify-between mt-1 rounded-lg px-3 py-2 text-sm ${over ? "bg-rose-50 text-rose-700 dark:bg-rose-900/30" : "bg-pink-50 text-pink-800 dark:bg-pink-900/20 dark:text-pink-200"}`}>
          <span>Total</span>
          <span className="font-bold" data-testid="pp-cat-total">{sum} / {max}{over ? " · exceeds max!" : ""}</span>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mt-1 cursor-pointer">
          <input type="checkbox" checked={absent} onChange={(e) => setAbsent(e.target.checked)} data-testid="pp-cat-absent" />
          Mark as Absent
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onApply(vals, absent)}
            disabled={over && !absent}
            className="bg-pink-600 hover:bg-pink-700"
            data-testid="pp-cat-apply"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivitiesDialog({ open, onClose, classSel, activities, onSaved }) {
  const [list, setList] = useState([]);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setList(activities || []); setVal(""); } }, [open, activities]);

  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (list.some((a) => a.toLowerCase() === v.toLowerCase())) { toast.message("Already added."); return; }
    setList((l) => [...l, v]);
    setVal("");
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/assessments/pre-primary/activities", { class_name: classSel, activities: list });
      toast.success("Activities saved.");
      onSaved(data.activities);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pp-activities-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="h-5 w-5 text-pink-600" /> Activity areas · {classSel}
          </DialogTitle>
          <DialogDescription>Graded each assessment with a letter (A+, A, B+, B, C, D) — no marks. Teachers pick the grade directly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {list.map((a, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm" data-testid={`pp-activity-row-${i}`}>
              <span className="text-slate-800 dark:text-slate-200">{a}</span>
              <button onClick={() => setList((l) => l.filter((_, idx) => idx !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`pp-activity-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
          {list.length === 0 && <div className="text-sm text-slate-400 text-center py-3">No activities yet.</div>}
        </div>
        <div className="flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add activity…" data-testid="pp-activity-input" />
          <Button variant="outline" onClick={add} data-testid="pp-activity-add"><Plus className="h-4 w-4" /></Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="pp-activities-save">{saving ? "Saving…" : "Save activities"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
